import { X } from "@phosphor-icons/react";
import "./PromotionModal.css";
import PromoImage from "./PromoImage";
import {
    audienceLabel,
    formatINRWhole,
    formatPrettyDate,
    offerLabel,
    sendPhaseLabel,
    lifecycleTriggerLabel,
    dailyWindowLabel,
    PROMO_STATUS,
} from "./promoUtils";

// Read-only detail view for a promotion (new promotions-engine contract).
export default function PromotionViewModal({ promo, onClose }) {
    if (!promo) return null;

    const Field = ({ label, value }) =>
        value ? (
            <div className="flex flex-col gap-1">
                <span className="text-[11px] font-bold text-gray-400 tracking-wide uppercase">{label}</span>
                <span className="text-sm text-gray-800">{value}</span>
            </div>
        ) : null;

    const showEarned =
        promo.status === PROMO_STATUS.ACTIVE || promo.status === PROMO_STATUS.ENDED;

    const hasWindow =
        promo.send_phase || promo.lifecycle_trigger || promo.time_window_start || promo.time_window_end;

    const targeting = [
        ...(promo.target_interests || []),
        ...(promo.target_purpose || []),
        ...(promo.target_travelling_with || []),
        ...(promo.target_dietary || []),
        ...(promo.target_arrival || []),
    ];

    return (
        <div className="fixed top-0 left-0 h-screen w-screen bg-black bg-opacity-40 flex items-center justify-center z-[1001]">
            <div className="promotionModal w-[40%] min-w-[360px] max-h-[90vh] popup-content bg-white p-6 rounded-2xl text-sm flex flex-col gap-4 overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold">Promotion Details</h2>
                    <button className="bg-[#F2F7FF] p-1.5 border-[1.5px] rounded-md" onClick={onClose}>
                        <X weight="bold" size={18} />
                    </button>
                </div>

                {/* Image */}
                <PromoImage
                    photoKey={promo.photo_url}
                    alt={promo.title}
                    className="h-[180px] rounded-lg border border-gray-200"
                />

                {/* Title + offer */}
                <div className="flex items-start justify-between gap-3">
                    <h3 className="text-lg font-semibold text-gray-800">{promo.title}</h3>
                    {offerLabel(promo) && (
                        <span className="shrink-0 text-xs font-bold text-[#FF5A5F] bg-[#FFF0F0] px-3 py-1 rounded-full">
                            {offerLabel(promo)}
                        </span>
                    )}
                </div>

                {/* Message */}
                {promo.text && (
                    <div className="flex flex-col gap-1">
                        <span className="text-[11px] font-bold text-gray-400 tracking-wide uppercase">Message</span>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{promo.text}</p>
                    </div>
                )}

                {/* Why tags */}
                {promo.reason_tags?.length > 0 && (
                    <div className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-bold text-[#7c3aed] tracking-wide uppercase">
                            Why this promotion?
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                            {promo.reason_tags.map((tag, i) => (
                                <span
                                    key={i}
                                    className="text-[11px] text-[#7c3aed] bg-[#f5f3ff] border border-[#e9d5ff] px-2 py-0.5 rounded-full"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Grid of fields */}
                <div className="grid grid-cols-2 gap-4 pt-1">
                    <Field label="Target Audience" value={audienceLabel(promo.target_audience)} />
                    <Field label="Service" value={promo.service} />
                    <Field label="Start Date" value={formatPrettyDate(promo.start_date)} />
                    <Field label="End Date" value={formatPrettyDate(promo.end_date)} />
                    {promo.min_order_amount > 0 && (
                        <Field label="Min Order" value={formatINRWhole(promo.min_order_amount)} />
                    )}
                    {promo.code && <Field label="Promo Code" value={promo.code} />}
                    <Field label="Sent Count" value={promo.sent_count?.toString()} />
                    {showEarned ? (
                        <>
                            <Field label="Upsell Generated" value={formatINRWhole(promo.upsell_generated)} />
                            <Field label="Bookings" value={(promo.conversions_count || 0).toString()} />
                        </>
                    ) : (
                        <>
                            {promo.est_revenue > 0 && (
                                <Field label="Est. Daily Revenue" value={`${formatINRWhole(promo.est_revenue)}/day`} />
                            )}
                            {promo.est_conversions > 0 && (
                                <Field label="Est. Daily Conversions" value={`${promo.est_conversions}/day`} />
                            )}
                        </>
                    )}
                </div>

                {/* Send / target window */}
                {hasWindow && (
                    <div className="flex flex-col gap-2 bg-[#F9FAFB] border border-gray-200 rounded-lg p-3">
                        <span className="text-[11px] font-bold text-gray-400 tracking-wide uppercase">
                            Send Window
                        </span>
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Phase" value={sendPhaseLabel(promo.send_phase)} />
                            <Field label="Timing" value={lifecycleTriggerLabel(promo.lifecycle_trigger)} />
                            <Field label="Daily Window" value={dailyWindowLabel(promo)} />
                        </div>
                    </div>
                )}

                {/* Targeting */}
                {targeting.length > 0 && (
                    <div className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-bold text-gray-400 tracking-wide uppercase">Targeting</span>
                        <div className="flex flex-wrap gap-1.5">
                            {targeting.map((t, i) => (
                                <span
                                    key={i}
                                    className="text-[11px] text-gray-600 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-full"
                                >
                                    {t}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
